<script module lang="ts">
	// Storyblok and R2 Assets URLs (assets.uilo.co or a custom domain) all carry /{width}x{height}/{hex id}/, and
	// all take Storyblok-style /m/ resizing
	export const resizable = (src: string) => /\/\d+x\d+\/[0-9a-f-]+\//i.test(src)

	// the URL to load: resized and cropped around the focus point when the host supports it, as-is otherwise
	export function resized(
		src: string,
		{
			width,
			height,
			quality,
			focus
		}: { width?: number; height?: number; quality: number; focus?: string | null }
	) {
		if (!resizable(src)) return src
		return `${src}/m/${width}x${height}/filters:quality(${quality})${focus ? `:focal(${focus})` : ''}`
	}
</script>

<script lang="ts">
	interface Props {
		src: string
		alt?: string | null
		fill?: boolean
		quality?: number
		mask?: string
		width?: number
		height?: number
		focus?: string | null
		aspect?: string
		contain?: boolean
	}

	let {
		src,
		alt,
		fill = undefined,
		quality = 85,
		mask = undefined,
		focus = undefined,
		aspect = undefined,
		contain = undefined,
		...rest
	}: Props = $props()

	const [src_width, src_height] = $derived(
		src
			.match(/(\d+)x(\d+)/i)
			?.slice(1)
			.map(Number) ?? [undefined, undefined]
	)

	const ratio: number = $derived.by(() => {
		if (aspect) {
			const [a, b] = aspect.split('/')?.map(Number) ?? []
			if (Number.isFinite(a) && Number.isFinite(b) && b !== 0) return a / b
		}

		if (src_width && src_height) return src_width / src_height

		return 0
	})

	const width = $derived(rest.width || Math.min(src_width ?? 0, 1600) || undefined)
	const height = $derived(contain ? 0 : rest.height || (ratio ? Math.round(width / ratio) : 0) || undefined)
	const href = $derived(resized(src, { width, height, quality, focus }))

	const position = $derived.by(() => {
		if (!focus || !src_width || !src_height) return
		// Parse focal point coordinates and calculate center
		const [p1, p2] = focus.split(':').map(xy => {
			const [x, y] = xy.split('x').map(Number)
			return { x, y }
		})
		const center = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 }

		// Calculate relative positions (0-1 range)
		const rel = { x: center.x / src_width, y: center.y / src_height }

		// Use percentage-based object-position for precision
		const x_pct = Math.max(0, Math.min(100, Math.round(rel.x * 100)))
		const y_pct = Math.max(0, Math.min(100, Math.round(rel.y * 100)))
		return `${x_pct}% ${y_pct}%`
	})

	// TODO: add mobile aspect and sizes
</script>

{#if mask}
	<image
		class="image"
		x="0"
		y="0"
		mask="url(#{mask})"
		preserveAspectRatio="xMidYMid slice"
		{href}
		{width}
		{height}
	/>
{:else}
	<img
		class="image"
		class:fill
		class:contain
		src={href}
		{alt}
		{width}
		{height}
		style:aspect-ratio={aspect}
		style:object-position={position}
		loading="lazy"
		decoding="async"
	/>
{/if}

<style lang="css">
	.image {
		object-fit: cover;
		object-position: center;

		&.fill {
			size: 100%;
		}

		&.contain {
			object-fit: contain;
		}
	}
</style>
